import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Feature,
  Layer,
  QueryableDataSourceOptions,
  SpatialFilterItemType,
  SpatialFilterQueryType,
  SpatialFilterService,
  SpatialFilterThematic,
  WMSDataSourceOptions
} from '@igo2/geo';
import { catchError, map, Observable, of } from 'rxjs';
import { SpatialFilterItemType as ExtendedSpatialFilterItemType }
  from 'src/app/integration/sigt-spatial-filter/sigt-spatial-filter-item/sigt-spatial-filter-item.component';
import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS';
import type { default as OlFeature } from 'ol/Feature';
import { dwithin, equalTo, intersects } from 'ol/format/filter';
import { Geometry, SimpleGeometry } from 'ol/geom';
import Filter from 'ol/format/filter/Filter';
import { DomainesService } from './domaines.service';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { LanguageService } from '@igo2/core/language';
import { ConfigService } from '@igo2/core/config';
import { MessageService } from '@igo2/core/message';
import { StorageService } from '@igo2/core/storage';

@Injectable({
  providedIn: 'root'
})
export class SpatialFilterExtensionService extends SpatialFilterService {
  sigtBaseUrl: string;

  constructor(
    private extensionHttp: HttpClient,
    private messageService: MessageService,
    private storageService: StorageService,
    private zzzConfigService: ZzzConfigService,
    private domainesService: DomainesService
  ) {
    super();
    this.sigtBaseUrl = this.zzzConfigService.get().authApiEndPoint;
  }

  loadFilterItem(
    feature,
    itemType: ExtendedSpatialFilterItemType,
    type?: SpatialFilterQueryType,
    thematic?: SpatialFilterThematic,
    buffer?: number,
    layers?: Layer[], //layer list from the map that must be queried.
    count?: number
  ) {
    // Override predefined regions when the value is our custom DirectionRegionalMRNF
    // and make it search as a drawn polygon search
    if (type as any === "DirectionRegionalMRNF") {
      type = undefined;
      feature = Object.assign(feature.geometry ? feature.geometry : feature, {
        "meta": {
          "title": "Zone"
        },
        "properties": {
          "nom": "Zone",
          "type": feature.geometry ? feature.geometry.type : feature.type
        }
      });
    }

    if (itemType === ExtendedSpatialFilterItemType.LieuxAllLayers ||
      itemType === ExtendedSpatialFilterItemType.LieuxVisibleLayers) {
      this.reinitialiserClesFicheDescriptive();
      try {
        this.storageService.set('sigtSpatialFilter_zone', JSON.stringify(feature));
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          this.messageService.error('Mémoire insuffisante dans le navigateur.');
        }
        console.error('An error occurred:', e);
        return of([]);
      }
      let quotaExceeded = false;
      if (layers?.length > 0) {
        let counter = 0;
        let currentLayerTitle = "";
        const layersWithIndex = layers.map((layer, index) => ({ layer, index }));
        try {
          layersWithIndex
            .filter(({ layer }) => layer.dataSource.options.type === 'wms'
              && (layer.dataSource.options as QueryableDataSourceOptions)?.queryable)
            .forEach(({ layer, index }) => {
              currentLayerTitle = layer.title;
              const layerOptions = layer.dataSource.options as WMSDataSourceOptions;
              const wfsUrl = layerOptions.urlWfs || "";
              if (wfsUrl !== "") {
                const paramsWFS = layerOptions.paramsWFS;
                //En principe quand on a plusieurs layer, ils partagent le même préfix
                const featurePrefix = paramsWFS.featureTypes.split(":")[0];
                const featureTypes = [paramsWFS.featureTypes];
                const srsName = paramsWFS.srsName || "EPSG:32198";
                const fieldNameGeometry = paramsWFS.fieldNameGeometry || "GEOMETRIE";
                const featureRequest = new WFS({ version: paramsWFS.version }).writeGetFeature({
                  srsName: srsName,
                  featureNS: wfsUrl,
                  featurePrefix: featurePrefix,
                  featureTypes: featureTypes,
                  outputFormat: 'application/json',
                  filter: this.getWFSGeomFilter(feature, fieldNameGeometry),
                  //startIndex:0,
                  count: count || paramsWFS.maxFeatures
                });
                this.storageService.set(`sigtSpatialFilter_featureRequestType${index}`, "wfs");
                this.storageService.set(`sigtSpatialFilter_featureRequestParams${index}`, new XMLSerializer().serializeToString(featureRequest));
                this.storageService.set(`sigtSpatialFilter_featureRequestUrl${index}`, wfsUrl);
              } else {
                this.storageService.set(`sigtSpatialFilter_featureRequestType${index}`, "wms");
                // add an empty URL to show a message that this search is not supported
                this.storageService.set(`sigtSpatialFilter_featureRequestUrl${index}`, '');
              }
              this.storageService.set(`sigtSpatialFilter_featureRequestTitle${index}`, layer.title);
              counter++;
              this.storageService.set(`sigtSpatialFilter_featureRequestsCount`, counter);
            });
        } catch (e) {
          if (e.name === 'QuotaExceededError') {
            this.messageService.error(`Mémoire insuffisante dans le navigateur: (${counter}) ${currentLayerTitle}`, undefined, { timeOut: 50000 });
          }
          console.error('An error occurred:', e);
          return of([]);
        }
        if (!quotaExceeded) {
          try {
            layersWithIndex
              .filter(({ layer }) => layer.dataSource.options.type === 'imagearcgisrest')
              .forEach(({ layer, index }) => {
                currentLayerTitle = layer.title;
                const layerOptions: any = layer.dataSource.options;
                let esriUrl = layerOptions.urlEsri;
                if (esriUrl !== undefined && esriUrl !== null) {
                  let paramsEsri = layerOptions.paramsEsri;
                  esriUrl = `${esriUrl}/${paramsEsri.layer}/query`;
                  const featureRequest = Object.assign({},
                    paramsEsri, this.getEsriGeomFilter(feature));
                  //cleanup
                  delete featureRequest["layer"];
                  delete featureRequest["queryFormat"];
                  const maxFeatures = count || paramsEsri.maxFeatures;
                  featureRequest["resultRecordCount"] = maxFeatures;
                  if (featureRequest.hasOwnProperty("maxFeatures")) {
                    delete featureRequest["maxFeatures"];
                  }
                  if (featureRequest.hasOwnProperty("srsName")) {
                    featureRequest["outSR"] = paramsEsri.srsName;
                    delete featureRequest["srsName"];
                  }
                  featureRequest["outFields"] = "*";
                  featureRequest["inSR"] = 4326;
                  featureRequest["f"] = "json";
                  this.storageService.set(`sigtSpatialFilter_featureRequestType${index}`, "arcgisrest");
                  this.storageService.set(`sigtSpatialFilter_featureRequestParams${index}`, featureRequest);
                }
                this.storageService.set(`sigtSpatialFilter_featureRequestTitle${index}`, layer.title);
                this.storageService.set(`sigtSpatialFilter_featureRequestUrl${index}`, esriUrl);
                counter++;
                this.storageService.set(`sigtSpatialFilter_featureRequestsCount`, counter);
              });
          } catch (e) {
            if (e.name === 'QuotaExceededError') {
              this.messageService.error(`Mémoire insuffisante dans le navigateur: (${counter}) ${currentLayerTitle}`, undefined, { timeOut: 50000 });
            }
            console.error('An error occurred:', e);
            return of([]);
          }
        }
      }
      window.open('/carte/?sa=fiche-descriptive');
      return of([]);
    } else {
      // igo2 20 : loadFilterItem(feature, itemType, type, thematic, buffer) est
      // devenu loadFilterItems(features[], itemType, { type, thematics, buffer }).
      return super.loadFilterItems([feature], itemType as SpatialFilterItemType, {
        type,
        thematics: thematic ? [thematic] : [],
        buffer
      });
    }
  }

  reinitialiserClesFicheDescriptive() {
    const pattern = /^igo\.(sigtSpatialFilter.*)$/;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const match = key.match(pattern);
        if (match && match[1]) {
          console.log(match[1]);
          this.storageService.remove(match[1]);
        }
      }
    }
  }

  getWFSGeomFilter(feature, fieldName: string): Filter {
    const filterSrsName = "urn:ogc:def:crs:OGC:1.3:CRS84";
    const geoJson = new GeoJSON();
    let geom: Geometry;
    let geomFilter: Filter;
    switch (feature.type) {
      case 'Feature': {
        geom = (geoJson.readFeature(feature) as OlFeature<Geometry>).getGeometry();
        geomFilter = intersects(fieldName, geom, filterSrsName);
        break;
      }
      case 'MultiPolygon':
      case 'Polygon': {
        geom = geoJson.readGeometry(feature);
        geomFilter = intersects(fieldName, geom, filterSrsName);
        break;
      }
      case 'Point':
        if (feature.radius) {
          geom = geoJson.readGeometry(feature);
          geomFilter = dwithin(fieldName, geom, feature.radius, "meters", filterSrsName);
          break;
        }
      default:
        throw ("Type de feature inconnu");
    }
    return geomFilter;
  }

  getEsriGeomFilter(feature): any {
    const spatialReference = { wkid: 4326 };
    const geoJson = new GeoJSON();
    var geom: Geometry;
    var esriFilter = {};
    switch (feature.type) {
      case 'Feature': {
        geom = this.convertToEsriGeometry((geoJson.readFeature(feature) as OlFeature<Geometry>).getGeometry(), spatialReference);
        esriFilter = {
          geometryType: "esriGeometryPolygon",
          geometry: geom,
          spatialRel: "esriSpatialRelIntersects"
        };
        break;
      }
      case 'MultiPolygon':
      case 'Polygon': {
        geom = this.convertToEsriGeometry(geoJson.readGeometry(feature), spatialReference);
        esriFilter = {
          geometryType: "esriGeometryPolygon",
          geometry: geom,
          spatialRel: "esriSpatialRelIntersects"
        };
        break;
      }
      case 'Point':
        if (feature.radius) {
          geom = this.convertToEsriGeometry(geoJson.readGeometry(feature), spatialReference);
          esriFilter = {
            geometryType: "esriGeometryPoint",
            geometry: geom,
            distance: feature.radius,
            units: "esriSRUnit_Meter",
            spatialRel: "esriSpatialRelIntersects"
          };
          break;
        }
      default:
        throw ("Type de feature inconnu");
    }
    return esriFilter;
  }

  private convertToEsriGeometry(geometry: Geometry, spatialReference): any {
    if (!(geometry instanceof SimpleGeometry)) {
      throw new Error(`Unsupported geometry type: ${geometry.getType()}`);
    }
    const type = geometry.getType();
    let esriGeometry: any;

    switch (type) {
      case 'Point':
        esriGeometry = {
          x: geometry.getCoordinates()[0],
          y: geometry.getCoordinates()[1],
          spatialReference: spatialReference
        };
        break;
      case 'LineString':
        esriGeometry = {
          paths: [geometry.getCoordinates()],
          spatialReference: spatialReference
        };
        break;
      case 'Polygon':
        esriGeometry = {
          rings: geometry.getCoordinates(),
          spatialReference: spatialReference
        };
        break;
      case 'MultiPolygon':
        esriGeometry = {
          rings: geometry.getCoordinates().flat(),
          spatialReference: spatialReference
        };
        break;
      default:
        throw new Error(`Unsupported geometry type: ${type}`);
    }

    return esriGeometry;
  }

  loadFilterList(type: SpatialFilterQueryType): Observable<Feature[]> {
    if (type as any === "DirectionRegionalMRNF") {
      return this.domainesService.obtenirValeurs("DIRECTION_REGIONALE")
        .pipe(
          map(data => {
            return data.valeursDomaine.map(entry => {
              let x: Feature = Object.assign({});
              x.properties = { nom: entry.description, ...entry };
              x.meta = { id: entry.valeurDomaineId };
              return x;
            });
          })
        );
    }
    return super.loadFilterList(type);
  }

  loadItemById(
    feature: Feature,
    type: SpatialFilterQueryType
  ): Observable<Feature> {
    if (type as any === "DirectionRegionalMRNF") {
      let wfsTool = new WFS({
        version: '2.0.0'
      });
      let wfsUrl = this.sigtBaseUrl + '/SIGT/wfs';
      const featureRequest = wfsTool.writeGetFeature({
        srsName: 'EPSG:4326',
        featureNS: wfsUrl,
        featurePrefix: 'SIGT',
        featureTypes: ['DirectionsRegionales'],
        outputFormat: 'application/json',
        filter: equalTo('CODE_DE_LA_DR', feature.properties.valeur)
      });
      const headers = new HttpHeaders({
        'interceptError': 'false'
      });

      return this.extensionHttp.post<{ features: Feature[] }>(
        wfsUrl,
        new XMLSerializer().serializeToString(featureRequest),
        { headers })
        .pipe(
          map(data => {
            let feature: Feature = Object.assign({}, data.features[0]);
            feature.properties.code = data.features[0].properties.CODE_DE_LA_DR;
            feature.properties.nom = data.features[0].properties.NOM_DE_LA_DR;
            delete feature.properties.CODE_DE_LA_DR;
            delete feature.properties.NOM_DE_LA_DR;
            feature.meta = {
              id: (feature as any).id,
              title: feature.properties.nom,
              alias: feature.properties.nom
            };
            return feature;
          }),
          catchError(_e => {
            return of(null);
          }));
    }
    return super.loadItemById(feature, type);
  }
}
